/**
 * Base API Client with JWT Authentication and automatic token refresh
 */

import type { APIError } from "./types";

/**
 * The filename a Content-Disposition header names, or null.
 *
 * `filename*` (RFC 5987, percent-encoded) wins when present and decodable;
 * otherwise the plain `filename`, taken verbatim inside its quotes so a
 * semicolon or a literal `%` in the name survives — percent-decoding a plain
 * filename like "100% return.pdf" throws, and a download must never fail over
 * its own name. Returns null rather than throwing on anything unexpected, so
 * the caller falls back to a name of its own.
 */
export function parseContentDispositionFilename(
  disposition: string | null,
): string | null {
  if (!disposition) return null;
  try {
    const extended = /filename\*\s*=\s*([^;]+)/i.exec(disposition);
    if (extended) {
      const value = extended[1].trim();
      const encoded = /^(?:UTF-8|ISO-8859-1)?'[^']*'(.*)$/i.exec(value);
      if (encoded) {
        try {
          const decoded = decodeURIComponent(encoded[1]);
          if (decoded) return stripPath(decoded);
        } catch {
          /* malformed encoding — fall through to the plain filename */
        }
      }
    }
    const quoted = /filename\s*=\s*"([^"]*)"/i.exec(disposition);
    if (quoted && quoted[1].trim()) return stripPath(quoted[1].trim());
    const bare = /filename\s*=\s*([^;]+)/i.exec(disposition);
    if (bare && bare[1].trim()) return stripPath(bare[1].trim());
  } catch {
    return null;
  }
  return null;
}

/** Only the basename: a server must not choose where the file lands. */
function stripPath(name: string): string | null {
  const base = name.split(/[/\\]/).pop()?.trim();
  return base ? base : null;
}

const API_BASE_URL = process.env.NEXT_PUBLIC_API_URL || "http://localhost:8000";
const AUTH_COOKIE_NAME = process.env.NEXT_PUBLIC_AUTH_COOKIE_NAME || "vpp_auth_token";
const REFRESH_TOKEN_KEY = "vpp_refresh_token";

export class ApiClient {
  private baseUrl: string;
  private refreshPromise: Promise<boolean> | null = null;

  constructor(baseUrl: string = API_BASE_URL) {
    this.baseUrl = baseUrl;
  }

  /**
   * Get the stored auth token
   */
  getToken(): string | null {
    if (typeof window === "undefined") return null;
    return localStorage.getItem(AUTH_COOKIE_NAME);
  }

  /**
   * Store the auth token
   */
  setToken(token: string): void {
    if (typeof window === "undefined") return;
    localStorage.setItem(AUTH_COOKIE_NAME, token);
  }

  /**
   * Get the stored refresh token
   */
  getRefreshToken(): string | null {
    if (typeof window === "undefined") return null;
    return localStorage.getItem(REFRESH_TOKEN_KEY);
  }

  /**
   * Store the refresh token
   */
  setRefreshToken(token: string): void {
    if (typeof window === "undefined") return;
    localStorage.setItem(REFRESH_TOKEN_KEY, token);
  }

  /**
   * Clear all auth tokens
   */
  clearToken(): void {
    if (typeof window === "undefined") return;
    localStorage.removeItem(AUTH_COOKIE_NAME);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
  }

  /**
   * Check if user is authenticated
   */
  isAuthenticated(): boolean {
    return !!this.getToken();
  }

  /**
   * Build headers with optional auth token
   */
  private buildHeaders(includeAuth: boolean = true, contentType: string = "application/json"): HeadersInit {
    const headers: HeadersInit = {
      "Content-Type": contentType,
    };

    if (includeAuth) {
      const token = this.getToken();
      if (token) {
        headers["Authorization"] = `Bearer ${token}`;
      }
    }

    return headers;
  }

  /**
   * Attempt to refresh the access token using the stored refresh token.
   * Deduplicates concurrent refresh attempts so only one request is in-flight.
   */
  private async tryRefreshToken(): Promise<boolean> {
    if (this.refreshPromise) {
      return this.refreshPromise;
    }

    this.refreshPromise = (async () => {
      const refreshToken = this.getRefreshToken();
      if (!refreshToken) return false;

      try {
        const response = await fetch(`${this.baseUrl}/api/v1/auth/refresh`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ refresh_token: refreshToken }),
        });

        if (!response.ok) return false;

        const data = await response.json();
        if (data.access_token) {
          this.setToken(data.access_token);
        }
        if (data.refresh_token) {
          this.setRefreshToken(data.refresh_token);
        }
        return true;
      } catch {
        return false;
      }
    })();

    try {
      return await this.refreshPromise;
    } finally {
      this.refreshPromise = null;
    }
  }

  /**
   * Parse error detail from a failed response.
   *
   * FastAPI returns 422 validation errors as `detail: [{loc, msg, type}, ...]`
   * rather than a plain string. Normalize that to a joined string so UIs that
   * render `APIError.detail` as a string don't show `[object Object]`.
   */
  private async parseError(response: Response): Promise<APIError> {
    let errorDetail: string = "An error occurred";
    try {
      const errorData = await response.json();
      const raw: unknown = errorData.detail ?? errorData.message;
      if (typeof raw === "string") {
        errorDetail = raw;
      } else if (Array.isArray(raw)) {
        // FastAPI 422 shape: list of {loc, msg, type}
        errorDetail = raw
          .map((e: unknown) => {
            if (typeof e === "string") return e;
            if (e && typeof e === "object" && "msg" in e) {
              return String((e as { msg: unknown }).msg);
            }
            return JSON.stringify(e);
          })
          .join("; ");
      } else if (raw && typeof raw === "object") {
        errorDetail = JSON.stringify(raw);
      }
    } catch {
      errorDetail = response.statusText || errorDetail;
    }
    return { detail: errorDetail, status_code: response.status };
  }

  /**
   * Central request method with automatic 401 retry via token refresh.
   *
   * @param method - HTTP method
   * @param endpoint - API endpoint
   * @param options.body - Request body (will be JSON.stringify'd unless rawBody is set)
   * @param options.rawBody - Pre-encoded body (for form data)
   * @param options.includeAuth - Whether to include auth token (default: true)
   * @param options.autoRedirectOn401 - Whether to redirect on unrecoverable 401 (default: matches includeAuth)
   * @param options.contentType - Content-Type header (default: application/json)
   */
  private async request<T>(
    method: string,
    endpoint: string,
    options: {
      body?: unknown;
      rawBody?: string | URLSearchParams;
      includeAuth?: boolean;
      autoRedirectOn401?: boolean;
      contentType?: string;
    } = {}
  ): Promise<T> {
    const {
      body,
      rawBody,
      includeAuth = true,
      contentType = "application/json",
    } = options;
    const autoRedirectOn401 = options.autoRedirectOn401 ?? includeAuth;

    const doFetch = () =>
      fetch(`${this.baseUrl}${endpoint}`, {
        method,
        headers: this.buildHeaders(includeAuth, contentType),
        body: rawBody ?? (body !== undefined ? JSON.stringify(body) : undefined),
      });

    let response = await doFetch();

    // On 401 with auth enabled, attempt a silent token refresh and retry once
    if (response.status === 401 && includeAuth && autoRedirectOn401) {
      const refreshed = await this.tryRefreshToken();
      if (refreshed) {
        response = await doFetch();
      }
    }

    if (!response.ok) {
      const error = await this.parseError(response);

      if (response.status === 401 && autoRedirectOn401) {
        this.clearToken();
        if (typeof window !== "undefined") {
          window.location.href = "/auth";
        }
      }

      throw error;
    }

    if (response.status === 204) {
      return {} as T;
    }

    return response.json();
  }

  /**
   * GET request
   */
  async get<T>(endpoint: string, includeAuth: boolean = true, autoRedirectOn401?: boolean): Promise<T> {
    return this.request<T>("GET", endpoint, {
      includeAuth,
      autoRedirectOn401: autoRedirectOn401 ?? includeAuth,
    });
  }

  /**
   * POST request
   */
  async post<T>(
    endpoint: string,
    body: unknown,
    includeAuth: boolean = true
  ): Promise<T> {
    return this.request<T>("POST", endpoint, {
      body,
      includeAuth,
      autoRedirectOn401: includeAuth,
    });
  }

  /**
   * POST form data (for OAuth2 token endpoint)
   */
  async postForm<T>(endpoint: string, formData: URLSearchParams): Promise<T> {
    return this.request<T>("POST", endpoint, {
      rawBody: formData,
      includeAuth: false,
      autoRedirectOn401: false,
      contentType: "application/x-www-form-urlencoded",
    });
  }

  /**
   * PUT request
   */
  async put<T>(
    endpoint: string,
    body: unknown,
    includeAuth: boolean = true
  ): Promise<T> {
    return this.request<T>("PUT", endpoint, { body, includeAuth });
  }

  /**
   * PATCH request
   */
  async patch<T>(
    endpoint: string,
    body: unknown,
    includeAuth: boolean = true
  ): Promise<T> {
    return this.request<T>("PATCH", endpoint, { body, includeAuth });
  }

  /**
   * DELETE request
   */
  async delete<T>(endpoint: string, includeAuth: boolean = true): Promise<T> {
    return this.request<T>("DELETE", endpoint, { includeAuth });
  }

  /**
   * Authenticated GET returning a binary body (file downloads).
   *
   * Goes through the same token, the same 401 refresh-and-retry and the same
   * error parsing as every other call, so a download does not need to know
   * where the token is stored. `filename` is the server's, read from
   * Content-Disposition — null when the header is absent or not exposed to
   * JS (cross-origin without `expose_headers`).
   */
  async getBlob(
    endpoint: string,
    includeAuth: boolean = true
  ): Promise<{ blob: Blob; filename: string | null }> {
    const doFetch = () =>
      fetch(`${this.baseUrl}${endpoint}`, {
        method: "GET",
        headers: this.buildHeaders(includeAuth),
      });

    let response = await doFetch();
    if (response.status === 401 && includeAuth) {
      const refreshed = await this.tryRefreshToken();
      if (refreshed) {
        response = await doFetch();
      }
    }

    if (!response.ok) {
      const error = await this.parseError(response);
      if (response.status === 401 && includeAuth) {
        this.clearToken();
        if (typeof window !== "undefined") {
          window.location.href = "/auth";
        }
      }
      throw error;
    }

    return {
      blob: await response.blob(),
      filename: parseContentDispositionFilename(
        response.headers.get("content-disposition")
      ),
    };
  }
}

// Singleton instance
export const apiClient = new ApiClient();
